import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Loader2,
  ExternalLink,
  Lamp,
  Link2,
  Crosshair,
  Plus,
  Pencil,
  Trash2,
  MoreHorizontal,
  SquareArrowOutUpRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { API_BASE } from '../../config/api';
import CricketRichCardModal from './CricketRichCardModal';
import CricketCardActionsMenu from './CricketCardActionsMenu';
import CricketBrandedBackdrop from './CricketBrandedBackdrop';
import { htmlToPlainPreview, parseCricketChecklist, cardCoverHex } from '../../utils/cricketCardUtils';
import {
  isCricketFreeStandaloneLayout,
  loadCricketFreeStandalone,
  saveCricketFreeStandalone,
  newStandaloneCricketId,
} from '../../utils/cricketFreeStandalone';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Largura da gaveta: ~3 colunas visíveis + “bico” da 4ª (slider horizontal). */
const LIST_TABS_VISIBLE_SLOTS = 3.25;

const CRICKET_LIST_LAMP_HEX = { todo: '#f59e0b', doing: '#3b82f6', done: '#22c55e' };

/** Desloca o painel «Lista de destino» um pouco mais para baixo (âncora acima do conteúdo). */
const CRICKET_MOVE_PANEL_EXTRA_DOWN_PX = 50;

/**
 * Gaveta resumida do Cricket (uma coluna por vez) + atalho para a visão Kanban completa.
 */
export default function CricketDrawer({
  open,
  onClose,
  layoutId,
  authToken,
  /** Plano Free: mesmo quadro no navegador (uma instância por conta), sem API de layout. */
  standaloneUserId,
  onOpenFullBoard,
  showToast,
  stageLampsHidden = false,
  onToggleStageLamps,
  onFocusLinkedWidget,
}) {
  const { t, i18n } = useTranslation(['cricket', 'builder']);
  const isStandalone = useMemo(
    () => Boolean(standaloneUserId && layoutId && isCricketFreeStandaloneLayout(layoutId)),
    [standaloneUserId, layoutId],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [sortedLists, setSortedLists] = useState([]);
  const [cards, setCards] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const drawerComposerRef = useRef(null);
  const [editingCardId, setEditingCardId] = useState(null);
  const [editTitleDraft, setEditTitleDraft] = useState('');
  const [moveMenuCardId, setMoveMenuCardId] = useState(null);
  const [moveTargetListId, setMoveTargetListId] = useState('');
  const [movePanelPos, setMovePanelPos] = useState(null);
  const [richModalCardId, setRichModalCardId] = useState(null);
  const [moreMenu, setMoreMenu] = useState(null);
  const [editingListId, setEditingListId] = useState(null);
  const [editListTitleDraft, setEditListTitleDraft] = useState('');
  const [newListDrawerOpen, setNewListDrawerOpen] = useState(false);
  const [newListDrawerTitle, setNewListDrawerTitle] = useState('');
  const listTabsViewportRef = useRef(null);
  const [listTabChipWidth, setListTabChipWidth] = useState(96);
  const [listTabsScrollState, setListTabsScrollState] = useState({ left: false, right: false });

  useEffect(() => {
    if (composerOpen && drawerComposerRef.current) {
      drawerComposerRef.current.focus();
    }
  }, [composerOpen]);

  useEffect(() => {
    setComposerOpen(false);
    setNewTitle('');
    setEditingCardId(null);
    setEditTitleDraft('');
    setMoveMenuCardId(null);
    setMovePanelPos(null);
    setRichModalCardId(null);
    setMoreMenu(null);
    setEditingListId(null);
    setNewListDrawerOpen(false);
    setNewListDrawerTitle('');
  }, [activeListId]);

  useEffect(() => {
    if (!open) {
      setComposerOpen(false);
      setNewTitle('');
      setEditingCardId(null);
      setMoveMenuCardId(null);
      setMovePanelPos(null);
      setRichModalCardId(null);
      setMoreMenu(null);
      setEditingListId(null);
      setNewListDrawerOpen(false);
      setNewListDrawerTitle('');
    }
  }, [open]);

  useEffect(() => {
    if (!moveMenuCardId) return undefined;
    const onDoc = (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      if (el.closest('[data-cricket-actions-root]')) return;
      if (el.closest('[data-cricket-drawer-move-portal]')) return;
      if (el.closest(`[data-cricket-drawer-card="${moveMenuCardId}"]`)) return;
      setMoveMenuCardId(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moveMenuCardId]);

  const notify = useCallback(
    (msg, type) => {
      if (typeof showToast === 'function') showToast(msg, type || 'warning');
    },
    [showToast],
  );

  const load = useCallback(async () => {
    if (!open) return;
    if (isStandalone) {
      if (!standaloneUserId) return;
      setLoading(true);
      setError(null);
      try {
        const { lists: rawLists, cards: rawCards } = loadCricketFreeStandalone(standaloneUserId);
        const lists = [...(rawLists || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setSortedLists(lists);
        setCards(rawCards || []);
        setReadOnly(false);
        setActiveListId((prev) => {
          if (prev && lists.some((l) => l.id === prev)) return prev;
          return lists[0]?.id || null;
        });
      } catch {
        setError('load');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!layoutId || !authToken) return;
    setLoading(true);
    setError(null);
    try {
      const [projRes, cricketRes] = await Promise.all([
        fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}`, { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      if (!projRes.ok || !cricketRes.ok) {
        const cr = await cricketRes.json().catch(() => ({}));
        if (cricketRes.status === 403) {
          setError((cr.error || '').toLowerCase().includes('plano') ? 'plan' : 'forbidden');
        } else {
          setError('load');
        }
        setLoading(false);
        return;
      }
      const projData = await projRes.json();
      const cp = projData.collabPermissions;
      setReadOnly(cp != null && cp.canEdit === false);

      const data = await cricketRes.json();
      const lists = [...(data.lists || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setSortedLists(lists);
      setCards(data.cards || []);
      setActiveListId((prev) => {
        if (prev && lists.some((l) => l.id === prev)) return prev;
        return lists[0]?.id || null;
      });
    } catch {
      setError('load');
    } finally {
      setLoading(false);
    }
  }, [open, layoutId, authToken, isStandalone, standaloneUserId]);

  const persistStandaloneCardPatch = useCallback(
    async (cardId, payload) => {
      if (!standaloneUserId) return false;
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        const ckJson =
          payload.checklist !== undefined
            ? payload.checklist == null
              ? null
              : JSON.stringify(payload.checklist)
            : undefined;
        const now = new Date().toISOString();
        const cardsNext = raw.cards.map((c) => {
          if (c.id !== cardId) return c;
          const next = { ...c, updated_at: now };
          if (payload.title !== undefined) next.title = String(payload.title || '').trim();
          if (payload.body !== undefined) next.body = payload.body;
          if (payload.list_id !== undefined) next.list_id = payload.list_id;
          if (payload.due_at !== undefined) next.due_at = payload.due_at;
          if (Object.prototype.hasOwnProperty.call(payload, 'cover_color')) {
            next.cover_color = payload.cover_color;
          }
          if (ckJson !== undefined) next.checklist_json = ckJson;
          return next;
        });
        saveCricketFreeStandalone(standaloneUserId, { lists: raw.lists, cards: cardsNext });
        await load();
        return true;
      } catch {
        return false;
      }
    },
    [standaloneUserId, load],
  );

  const duplicateStandaloneCard = useCallback(
    async (cardId, listIdParam) => {
      if (!standaloneUserId) return false;
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        const src = raw.cards.find((c) => c.id === cardId);
        if (!src) return false;
        const destListId = listIdParam || src.list_id;
        const newId = newStandaloneCricketId();
        const sameList = raw.cards.filter((c) => c.list_id === destListId);
        const sortOrder =
          sameList.length > 0 ? Math.max(...sameList.map((c) => Number(c.sort_order) || 0)) + 1 : 0;
        const now = new Date().toISOString();
        const copy = {
          ...src,
          id: newId,
          list_id: destListId,
          widget_id: null,
          sort_order: sortOrder,
          created_at: now,
          updated_at: now,
        };
        saveCricketFreeStandalone(standaloneUserId, { lists: raw.lists, cards: [...raw.cards, copy] });
        await load();
        return true;
      } catch {
        return false;
      }
    },
    [standaloneUserId, load],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (richModalCardId) {
        setRichModalCardId(null);
        return;
      }
      if (moreMenu) {
        setMoreMenu(null);
        return;
      }
      if (editingCardId) {
        setEditingCardId(null);
        return;
      }
      if (moveMenuCardId) {
        setMoveMenuCardId(null);
        return;
      }
      if (editingListId) {
        setEditingListId(null);
        return;
      }
      if (newListDrawerOpen) {
        setNewListDrawerOpen(false);
        setNewListDrawerTitle('');
        return;
      }
      if (composerOpen) {
        setComposerOpen(false);
        setNewTitle('');
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, richModalCardId, moreMenu, editingCardId, moveMenuCardId, composerOpen, editingListId, newListDrawerOpen]);

  const cardsInActive = useMemo(() => {
    if (!activeListId) return [];
    return [...cards]
      .filter((c) => c.list_id === activeListId)
      .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || String(a.created_at || '').localeCompare(String(b.created_at || '')));
  }, [cards, activeListId]);

  useLayoutEffect(() => {
    if (!moveMenuCardId) {
      setMovePanelPos(null);
      return;
    }
    const cardEl = document.querySelector(`[data-cricket-drawer-card="${moveMenuCardId}"]`);
    if (!cardEl) {
      setMovePanelPos(null);
      return;
    }
    const w = 236;
    const gap = 6;
    /** Altura aproximada do painel (select + botões); evita cortar no topo da viewport. */
    const minSpaceAbove = 200;
    const place = () => {
      const anchor =
        cardEl.querySelector('[data-cricket-drawer-move-anchor]') || cardEl;
      const r = anchor.getBoundingClientRect();
      const cardR = cardEl.getBoundingClientRect();
      const left = Math.max(8, Math.min(cardR.right - w, window.innerWidth - w - 8));
      if (r.top >= minSpaceAbove + gap) {
        setMovePanelPos({
          placement: 'above',
          left,
          bottom: window.innerHeight - r.top + gap - CRICKET_MOVE_PANEL_EXTRA_DOWN_PX,
        });
      } else {
        setMovePanelPos({
          placement: 'below',
          left,
          top: cardR.bottom + gap + CRICKET_MOVE_PANEL_EXTRA_DOWN_PX,
        });
      }
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [moveMenuCardId, cardsInActive, activeListId]);

  const listKeyById = useMemo(() => Object.fromEntries((sortedLists || []).map((l) => [l.id, l.list_key])), [sortedLists]);

  const handleAddCard = async () => {
    if (readOnly || !activeListId) return;
    if (!isStandalone && !authToken) return;
    const title = newTitle.trim();
    if (!title) return;
    if (isStandalone && standaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        const listCards = raw.cards.filter((c) => c.list_id === activeListId);
        const sortOrder =
          listCards.length > 0 ? Math.max(...listCards.map((c) => Number(c.sort_order) || 0)) + 1 : 0;
        const now = new Date().toISOString();
        const newCard = {
          id: newStandaloneCricketId(),
          list_id: activeListId,
          widget_id: null,
          title,
          body: null,
          sort_order: sortOrder,
          cover_color: null,
          due_at: null,
          checklist_json: null,
          created_at: now,
          updated_at: now,
        };
        saveCricketFreeStandalone(standaloneUserId, { lists: raw.lists, cards: [...raw.cards, newCard] });
        setNewTitle('');
        setComposerOpen(false);
        await load();
      } catch {
        notify(t('cricket:createError'), 'error');
      }
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ list_id: activeListId, title }),
      });
      if (!res.ok) {
        notify(t('cricket:createError'), 'error');
        return;
      }
      setNewTitle('');
      setComposerOpen(false);
      await load();
    } catch {
      notify(t('cricket:createError'), 'error');
    }
  };

  const commitCardTitle = async (cardId) => {
    if (readOnly) return;
    const title = editTitleDraft.trim();
    if (!title) return;
    if (isStandalone && standaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        const now = new Date().toISOString();
        const cardsNext = raw.cards.map((c) =>
          c.id === cardId ? { ...c, title, updated_at: now } : c,
        );
        saveCricketFreeStandalone(standaloneUserId, { lists: raw.lists, cards: cardsNext });
        setEditingCardId(null);
        await load();
      } catch {
        notify(t('cricket:syncError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(
        `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(cardId)}`,
        { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify({ title }) },
      );
      if (!res.ok) {
        notify(t('cricket:syncError'), 'error');
        return;
      }
      setEditingCardId(null);
      await load();
    } catch {
      notify(t('cricket:syncError'), 'error');
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (readOnly) return;
    if (isStandalone && standaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        saveCricketFreeStandalone(standaloneUserId, {
          lists: raw.lists,
          cards: raw.cards.filter((c) => c.id !== cardId),
        });
        setMoveMenuCardId(null);
        setEditingCardId(null);
        setMoreMenu((m) => (m && m.cardId === cardId ? null : m));
        setRichModalCardId((id) => (id === cardId ? null : id));
        await load();
      } catch {
        notify(t('cricket:deleteError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(
        `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(cardId)}`,
        { method: 'DELETE', headers: authHeaders(authToken) },
      );
      if (!res.ok) {
        notify(t('cricket:deleteError'), 'error');
        return;
      }
      setMoveMenuCardId(null);
      setEditingCardId(null);
      setMoreMenu((m) => (m && m.cardId === cardId ? null : m));
      setRichModalCardId((id) => (id === cardId ? null : id));
      await load();
    } catch {
      notify(t('cricket:deleteError'), 'error');
    }
  };

  const openMoveMenu = (cardId) => {
    const others = sortedLists.filter((l) => l.id !== activeListId);
    setMoveTargetListId(others[0]?.id || '');
    setMoveMenuCardId(cardId);
    setEditingCardId(null);
  };

  const commitDrawerListTitle = async (listId) => {
    if (readOnly) return;
    const prev = sortedLists.find((l) => l.id === listId);
    const title = editListTitleDraft.trim();
    if (!title) {
      setEditingListId(null);
      return;
    }
    if (prev && prev.title === title) {
      setEditingListId(null);
      return;
    }
    if (isStandalone && standaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        const listsNext = raw.lists.map((l) => (l.id === listId ? { ...l, title } : l));
        saveCricketFreeStandalone(standaloneUserId, { lists: listsNext, cards: raw.cards });
        setEditingListId(null);
        await load();
      } catch {
        notify(t('cricket:listSyncError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(
        `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/lists/${encodeURIComponent(listId)}`,
        { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify({ title }) },
      );
      if (!res.ok) {
        notify(t('cricket:listSyncError'), 'error');
        return;
      }
      setEditingListId(null);
      await load();
    } catch {
      notify(t('cricket:listSyncError'), 'error');
    }
  };

  const handleCreateDrawerList = async () => {
    if (readOnly) return;
    const title = newListDrawerTitle.trim();
    if (!title) return;
    if (isStandalone && standaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        const newList = {
          id: newStandaloneCricketId(),
          board_id: null,
          list_key: 'custom',
          title,
          sort_order: raw.lists.length,
        };
        saveCricketFreeStandalone(standaloneUserId, { lists: [...raw.lists, newList], cards: raw.cards });
        setNewListDrawerTitle('');
        setNewListDrawerOpen(false);
        setActiveListId(newList.id);
        await load();
      } catch {
        notify(t('cricket:createListError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/lists`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ title }),
      });
      if (!res.ok) {
        notify(t('cricket:createListError'), 'error');
        return;
      }
      const data = await res.json().catch(() => ({}));
      setNewListDrawerTitle('');
      setNewListDrawerOpen(false);
      if (data.list?.id) setActiveListId(data.list.id);
      await load();
    } catch {
      notify(t('cricket:createListError'), 'error');
    }
  };

  const commitMoveCard = async (cardId) => {
    if (readOnly || !moveTargetListId || moveTargetListId === activeListId) return;
    if (isStandalone && standaloneUserId) {
      try {
        const raw = loadCricketFreeStandalone(standaloneUserId);
        const destCards = raw.cards.filter((c) => c.list_id === moveTargetListId && c.id !== cardId);
        const nextSort =
          destCards.length > 0 ? Math.max(...destCards.map((c) => Number(c.sort_order) || 0)) + 1 : 0;
        const now = new Date().toISOString();
        const cardsNext = raw.cards.map((c) =>
          c.id === cardId ? { ...c, list_id: moveTargetListId, sort_order: nextSort, updated_at: now } : c,
        );
        saveCricketFreeStandalone(standaloneUserId, { lists: raw.lists, cards: cardsNext });
        setMoveMenuCardId(null);
        setActiveListId(moveTargetListId);
        await load();
      } catch {
        notify(t('cricket:syncError'), 'error');
      }
      return;
    }
    if (!authToken) return;
    try {
      const res = await fetch(
        `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(cardId)}`,
        { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify({ list_id: moveTargetListId }) },
      );
      if (!res.ok) {
        notify(t('cricket:syncError'), 'error');
        return;
      }
      setMoveMenuCardId(null);
      setActiveListId(moveTargetListId);
      await load();
    } catch {
      notify(t('cricket:syncError'), 'error');
    }
  };

  const otherListsForMove = useMemo(() => sortedLists.filter((l) => l.id !== activeListId), [sortedLists, activeListId]);

  const updateListTabsScrollArrows = useCallback(() => {
    const el = listTabsViewportRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    setListTabsScrollState({
      left: scrollLeft > 4,
      right: maxScroll > 4 && scrollLeft < maxScroll - 4,
    });
  }, []);

  const scrollListTabsBy = useCallback((direction) => {
    const el = listTabsViewportRef.current;
    if (!el) return;
    const step = Math.max(96, Math.floor(el.clientWidth * 0.65)) * direction;
    el.scrollBy({ left: step, behavior: 'smooth' });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const el = listTabsViewportRef.current;
    if (!el) return undefined;
    const apply = () => {
      const w = el.clientWidth;
      if (w > 0) {
        setListTabChipWidth(Math.max(72, Math.floor(w / LIST_TABS_VISIBLE_SLOTS)));
      }
      updateListTabsScrollArrows();
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, sortedLists.length, editingListId, newListDrawerOpen, updateListTabsScrollArrows]);

  useEffect(() => {
    if (!open) return undefined;
    const el = listTabsViewportRef.current;
    if (!el) return undefined;
    const onScroll = () => updateListTabsScrollArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [open, updateListTabsScrollArrows, sortedLists.length]);

  useEffect(() => {
    if (!open || !activeListId) return;
    const id = requestAnimationFrame(() => {
      const viewport = listTabsViewportRef.current;
      if (!viewport) return;
      const tabEl = viewport.querySelector(`[data-cricket-drawer-list-tab="${activeListId}"]`);
      if (!(tabEl instanceof HTMLElement)) return;
      const vr = viewport.getBoundingClientRect();
      const tr = tabEl.getBoundingClientRect();
      if (tr.left < vr.left + 2 || tr.right > vr.right - 2) {
        tabEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open, activeListId, sortedLists]);

  useLayoutEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => updateListTabsScrollArrows());
    return () => cancelAnimationFrame(id);
  }, [open, sortedLists, updateListTabsScrollArrows]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[1390] bg-slate-900/35 backdrop-blur-[2px] transition-opacity dark:bg-black/40"
        aria-label={t('cricket:closeDrawer')}
        onClick={onClose}
      />
      <aside
        className="fixed top-0 right-0 z-[1395] flex h-full w-full max-w-md flex-col overflow-hidden border-l border-white/25 shadow-2xl dark:border-slate-600/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cricket-drawer-title"
      >
        <CricketBrandedBackdrop />
        <div className="relative z-10 flex min-h-0 flex-1 flex-col bg-white/90 backdrop-blur-lg dark:bg-slate-900/90">
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div className="flex min-w-0 items-center gap-2">
            <Lamp className="h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden />
            <h2 id="cricket-drawer-title" className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {t('cricket:pageTitle')}
            </h2>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {typeof onToggleStageLamps === 'function' && (
              <button
                type="button"
                onClick={onToggleStageLamps}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                title={stageLampsHidden ? t('cricket:showStageLamps') : t('cricket:hideStageLamps')}
                aria-pressed={!stageLampsHidden}
                aria-label={stageLampsHidden ? t('cricket:showStageLamps') : t('cricket:hideStageLamps')}
              >
                <Lamp
                  size={20}
                  className={stageLampsHidden ? 'opacity-35' : 'text-amber-500'}
                  aria-hidden
                />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label={t('cricket:closeDrawer')}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-stretch gap-0.5 border-b border-slate-100 py-2 pl-1 pr-0.5 dark:border-slate-800">
          <button
            type="button"
            disabled={!listTabsScrollState.left}
            onClick={() => scrollListTabsBy(-1)}
            className="flex shrink-0 items-center justify-center rounded-lg px-1 py-1 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-25 dark:hover:bg-slate-800"
            aria-label={t('cricket:scrollListsPrev')}
            title={t('cricket:scrollListsPrev')}
          >
            <ChevronLeft size={20} aria-hidden />
          </button>
          <div
            ref={listTabsViewportRef}
            className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="inline-flex min-h-[2.25rem] items-stretch gap-1 px-0.5">
              {sortedLists.map((list) =>
                editingListId === list.id && !readOnly ? (
                  <input
                    key={list.id}
                    data-cricket-drawer-list-tab={list.id}
                    className="shrink-0 rounded-lg border border-blue-400 bg-white px-2 py-2 text-center text-xs font-semibold text-slate-800 dark:bg-slate-950 dark:text-slate-100"
                    style={{ width: listTabChipWidth, minWidth: listTabChipWidth, maxWidth: listTabChipWidth }}
                    value={editListTitleDraft}
                    onChange={(e) => setEditListTitleDraft(e.target.value)}
                    onBlur={() => void commitDrawerListTitle(list.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void commitDrawerListTitle(list.id);
                      }
                      if (e.key === 'Escape') setEditingListId(null);
                    }}
                    autoFocus
                    aria-label={t('cricket:renameColumn')}
                  />
                ) : (
                  <button
                    key={list.id}
                    type="button"
                    data-cricket-drawer-list-tab={list.id}
                    onClick={() => setActiveListId(list.id)}
                    onDoubleClick={() => {
                      if (readOnly) return;
                      setEditingListId(list.id);
                      setEditListTitleDraft(list.title || '');
                    }}
                    style={{ width: listTabChipWidth, minWidth: listTabChipWidth, maxWidth: listTabChipWidth }}
                    className={`shrink-0 truncate rounded-lg px-1.5 py-2 text-center text-xs font-semibold transition-colors ${
                      activeListId === list.id
                        ? 'bg-amber-500 text-white shadow-sm dark:bg-amber-600'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                    title={`${list.title} — ${t('cricket:renameHint')}`}
                  >
                    {list.title}
                  </button>
                ),
              )}
              {!readOnly &&
                (newListDrawerOpen ? (
                  <div
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-300 bg-white/95 px-1 py-1 dark:border-slate-600 dark:bg-slate-950/90"
                    style={{ minWidth: Math.min(280, Math.floor(listTabChipWidth * 2.4)) }}
                  >
                    <input
                      className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                      placeholder={t('cricket:listTitlePlaceholder')}
                      value={newListDrawerTitle}
                      onChange={(e) => setNewListDrawerTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleCreateDrawerList();
                        }
                        if (e.key === 'Escape') {
                          setNewListDrawerOpen(false);
                          setNewListDrawerTitle('');
                        }
                      }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => void handleCreateDrawerList()}
                      className="shrink-0 rounded-md bg-blue-600 px-2 py-1.5 text-[10px] font-semibold text-white hover:bg-blue-700"
                    >
                      {t('cricket:addColumnSubmit')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setNewListDrawerOpen(true)}
                    style={{ width: listTabChipWidth, minWidth: listTabChipWidth, maxWidth: listTabChipWidth }}
                    className="shrink-0 rounded-lg bg-slate-100 py-2 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    title={t('cricket:addColumn')}
                    aria-label={t('cricket:addColumn')}
                  >
                    <Plus size={16} className="mx-auto" aria-hidden />
                  </button>
                ))}
            </div>
          </div>
          <button
            type="button"
            disabled={!listTabsScrollState.right}
            onClick={() => scrollListTabsBy(1)}
            className="flex shrink-0 items-center justify-center rounded-lg px-1 py-1 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-25 dark:hover:bg-slate-800"
            aria-label={t('cricket:scrollListsNext')}
            title={t('cricket:scrollListsNext')}
          >
            <ChevronRight size={20} aria-hidden />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-[#ebecf0] dark:bg-slate-900/40">
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" aria-hidden />
              </div>
            )}
            {!loading && error === 'plan' && (
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('cricket:planLocked')}</p>
            )}
            {!loading && error === 'forbidden' && (
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('cricket:forbidden')}</p>
            )}
            {!loading && error === 'load' && (
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('cricket:loadError')}</p>
            )}
            {!loading && !error && readOnly && (
              <p className="mb-3 text-xs text-amber-800 dark:text-amber-200/90">{t('cricket:readOnlyHint')}</p>
            )}
            {!loading && !error && (
              <>
                <ul className="space-y-2">
                  {cardsInActive.length === 0 && !composerOpen && (
                    <li className="py-4 text-center text-xs text-slate-500 dark:text-slate-400">{t('cricket:emptyColumn')}</li>
                  )}
                  {cardsInActive.map((c) => {
                    const checklistItems = parseCricketChecklist(c.checklist_json);
                    const checklistDone = checklistItems.filter((x) => x.done).length;
                    const coverHex = cardCoverHex(c.cover_color);
                    let dueLabel = null;
                    if (c.due_at) {
                      const d = new Date(String(c.due_at).replace(' ', 'T'));
                      if (!Number.isNaN(d.getTime())) {
                        dueLabel = d.toLocaleDateString(i18n.resolvedLanguage || i18n.language, {
                          day: 'numeric',
                          month: 'short',
                        });
                      }
                    }
                    return (
                    <li
                      key={c.id}
                      data-cricket-drawer-card={c.id}
                      className={`relative rounded-lg border-0 bg-white px-3 py-2 text-sm text-slate-800 shadow-[0_1px_0_#091e4240] dark:bg-slate-900 dark:text-slate-100 dark:shadow-[0_1px_0_rgba(0,0,0,0.4)] ${
                        editingCardId === c.id ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-[#ebecf0] dark:ring-offset-slate-900' : 'group'
                      }`}
                    >
                      {!!coverHex && (
                        <div
                          className="mb-2 min-h-[52px] w-full rounded-lg"
                          style={{ backgroundColor: coverHex }}
                          aria-hidden
                        />
                      )}
                      {editingCardId !== c.id && (
                        <div className="pointer-events-none absolute right-1.5 top-1.5 z-10 flex gap-0.5 rounded-md border border-slate-200/80 bg-white/95 p-0.5 shadow-sm opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 dark:border-slate-600 dark:bg-slate-800/95">
                          <button
                            type="button"
                            className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                            title={t('cricket:moreCardActions')}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              const r = e.currentTarget.getBoundingClientRect();
                              setMoveMenuCardId(null);
                              setMoreMenu({
                                cardId: c.id,
                                listId: activeListId,
                                top: r.bottom + 4,
                                left: r.right - 224,
                              });
                            }}
                          >
                            <MoreHorizontal size={14} aria-hidden />
                          </button>
                          <button
                            type="button"
                            disabled={readOnly}
                            className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                            title={t('cricket:editCard')}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoveMenuCardId(null);
                              setEditingCardId(c.id);
                              setEditTitleDraft(c.title || '');
                            }}
                          >
                            <Pencil size={14} aria-hidden />
                          </button>
                          {!!c.widget_id && typeof onFocusLinkedWidget === 'function' && (
                            <button
                              type="button"
                              className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-950/50 dark:hover:text-amber-300"
                              title={t('cricket:goToStageWidget')}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                onFocusLinkedWidget(c.widget_id);
                              }}
                            >
                              <Crosshair size={14} aria-hidden />
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={readOnly}
                            className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                            title={t('cricket:deleteCard')}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCard(c.id);
                            }}
                          >
                            <Trash2 size={14} aria-hidden />
                          </button>
                        </div>
                      )}
                      {editingCardId === c.id ? (
                        <div className="space-y-2 pr-1 pt-0.5">
                          <textarea
                            className="w-full min-h-[4.5rem] resize-y rounded-md border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800 shadow-inner focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                            value={editTitleDraft}
                            onChange={(e) => setEditTitleDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                commitCardTitle(c.id);
                              }
                            }}
                            rows={3}
                            aria-label={t('cricket:editCard')}
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => commitCardTitle(c.id)}
                              className="rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
                            >
                              {t('cricket:saveCardTitle')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingCardId(null)}
                              className="rounded px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                              {t('cricket:cancelComposer')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="flex items-start gap-2 pr-14"
                          data-cricket-drawer-move-anchor
                        >
                          <button
                            type="button"
                            className="mt-0.5 shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                            title={t('cricket:openCard')}
                            aria-label={t('cricket:openCard')}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoveMenuCardId(null);
                              setMoreMenu(null);
                              setRichModalCardId(c.id);
                            }}
                          >
                            <SquareArrowOutUpRight size={16} aria-hidden />
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="break-words font-medium">{c.title}</div>
                            {(dueLabel || checklistItems.length > 0) && (
                              <div className="mt-1 flex flex-wrap gap-2 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                                {dueLabel && (
                                  <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">{dueLabel}</span>
                                )}
                                {checklistItems.length > 0 && (
                                  <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">
                                    ✓ {checklistDone}/{checklistItems.length}
                                  </span>
                                )}
                              </div>
                            )}
                            {(() => {
                              const raw = c.body && String(c.body).trim();
                              if (!raw) return null;
                              const prev = htmlToPlainPreview(raw);
                              if (!prev) return null;
                              return (
                                <p
                                  className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-[11px] font-normal leading-snug text-slate-500 dark:text-slate-400"
                                  title={prev}
                                >
                                  {prev}
                                </p>
                              );
                            })()}
                            {!!c.widget_id && (
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                                <span className="inline-flex items-center gap-0.5" title={t('cricket:linkedToElement')}>
                                  <Link2 size={12} aria-hidden />
                                  {t('cricket:linkedToElement')}
                                </span>
                                <Lamp
                                  size={12}
                                  className="flex-shrink-0"
                                  style={{ color: CRICKET_LIST_LAMP_HEX[listKeyById[c.list_id]] || '#a855f7' }}
                                  aria-hidden
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                    );
                  })}
                </ul>
                {!readOnly &&
                  (composerOpen ? (
                    <div className="mt-2 rounded-lg border border-slate-300 bg-white p-2 shadow-[0_1px_0_#091e4240] dark:border-slate-600 dark:bg-slate-900">
                      <input
                        ref={drawerComposerRef}
                        className="w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                        placeholder={t('cricket:cardTitlePlaceholder')}
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCard();
                          }
                          if (e.key === 'Escape') {
                            setComposerOpen(false);
                            setNewTitle('');
                          }
                        }}
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleAddCard()}
                          className="rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
                        >
                          {t('cricket:addCardSubmit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setComposerOpen(false);
                            setNewTitle('');
                          }}
                          className="rounded px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          {t('cricket:cancelComposer')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setComposerOpen(true);
                        setNewTitle('');
                      }}
                      className="mt-2 flex w-full items-center gap-2 rounded-lg py-2 pl-2 pr-2 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-[#00000014] dark:text-slate-300 dark:hover:bg-white/10"
                    >
                      <Plus size={18} strokeWidth={2.25} className="shrink-0 opacity-70" aria-hidden />
                      {t('cricket:addCardLink')}
                    </button>
                  ))}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 gap-2 border-t border-slate-200 p-4 dark:border-slate-700">
          <button
            type="button"
            onClick={() => {
              onClose();
              onOpenFullBoard();
            }}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <ExternalLink size={16} />
            {t('cricket:openFullBoard')}
          </button>
        </div>

        <CricketRichCardModal
          open={!!richModalCardId && !!cards.find((x) => x.id === richModalCardId)}
          card={richModalCardId ? cards.find((x) => x.id === richModalCardId) || null : null}
          sortedLists={sortedLists}
          layoutId={layoutId}
          authToken={authToken}
          readOnly={readOnly}
          onClose={() => setRichModalCardId(null)}
          onSaved={load}
          onFocusLinkedWidget={onFocusLinkedWidget}
          showToast={showToast}
          persistCardPatch={isStandalone ? persistStandaloneCardPatch : undefined}
        />
        {moreMenu && cards.find((x) => x.id === moreMenu.cardId) ? (
          <CricketCardActionsMenu
            open
            anchor={{ top: moreMenu.top, left: moreMenu.left }}
            onClose={() => setMoreMenu(null)}
            readOnly={readOnly}
            layoutId={layoutId}
            authToken={authToken}
            card={cards.find((x) => x.id === moreMenu.cardId)}
            listId={moreMenu.listId}
            sortedLists={sortedLists}
            onOpenMove={() => {
              const mid = moreMenu.cardId;
              setMoreMenu(null);
              openMoveMenu(mid);
            }}
            onAfterMutation={load}
            showToast={showToast}
            persistCardPatch={isStandalone ? persistStandaloneCardPatch : undefined}
            duplicateCardLocal={isStandalone ? duplicateStandaloneCard : undefined}
          />
        ) : null}
        </div>
      </aside>

      {moveMenuCardId &&
        movePanelPos &&
        otherListsForMove.length > 0 &&
        createPortal(
          <div
            data-cricket-drawer-move-portal
            role="dialog"
            aria-label={t('cricket:moveCardToList')}
            className="fixed z-[21000] min-w-[220px] rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-600 dark:bg-slate-900"
            style={
              movePanelPos.placement === 'above'
                ? { left: movePanelPos.left, bottom: movePanelPos.bottom, top: 'auto' }
                : { left: movePanelPos.left, top: movePanelPos.top, bottom: 'auto' }
            }
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mb-2 text-xs font-semibold text-slate-700 dark:text-slate-200">{t('cricket:moveCardToList')}</div>
            <select
              className="mb-3 w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
              value={moveTargetListId}
              onChange={(e) => setMoveTargetListId(e.target.value)}
            >
              {otherListsForMove.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => commitMoveCard(moveMenuCardId)}
                className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
              >
                {t('cricket:moveCardSubmit')}
              </button>
              <button
                type="button"
                onClick={() => setMoveMenuCardId(null)}
                className="rounded px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {t('cricket:cancelComposer')}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
